import React from "react";
import { CheckCircle } from "lucide-react";
import QRCode from "react-qr-code";
import { Link, useSearchParams } from "react-router-dom";
import { useOrderDetails } from "../lib/api/ticket";
import ConfirmationSkeleton from "../components/tickets/ConfirmationSkeleton";

export default function Confirmation() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const { data, isLoading, error } = useOrderDetails(sessionId);

  if (isLoading) {
    return <ConfirmationSkeleton />;
  }

  if (error || !data) {
    return (
      <section className="bg-gradient-to-b from-[#f1f1f1] to-[#F5F3F0] flex items-center justify-center py-16 px-6">
        <div className="max-w-5xl w-full bg-white/90 backdrop-blur-md border-4 border-red-300 shadow-xl p-10 mt-6 text-center">
          <h1 className="text-4xl font-serif text-red-600 mb-4">
            Error Loading Order
          </h1>
          <p className="text-gray-700">
            {error?.response?.data?.message ||
              "Unable to load order details. Please try again."}
          </p>
        </div>
      </section>
    );
  }

  const order = data.data;

  const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;

  return (
    <section className="bg-gradient-to-b from-[#f1f1f1] to-[#F5F3F0] flex items-center justify-center py-16 px-6">
      <div className="max-w-6xl w-full bg-white/90 backdrop-blur-md border-4 border-[#B9A68D] shadow-xl px-5 py-10 mt-6">
        {/* Success Header */}
        <div className="text-center mb-10">
          <CheckCircle className="mx-auto h-16 w-16 text-[#4E5C36] mb-6" />
          <h1 className="text-3xl md:text-4xl font-serif text-[#4E5C36] mb-2">
            Payment Successful
          </h1>
          <p className="text-gray-700 italic">
            Thank you for your purchase! Below are your digital tickets.
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-gradient-to-r from-[#F7F6F4] to-[#EDE7DD] p-6 rounded-md shadow-sm mb-12">
          <h2 className="text-2xl font-semibold text-[#4E5C36] mb-6">
            Order Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
            <p>
              <span className="font-medium">Ticket Type:</span> {order?.tier}
            </p>
            <p>
              <span className="font-medium">Quantity:</span> {order?.quantity}
            </p>
            <p>
              <span className="font-medium">Total Paid:</span> £
              {order?.amountTotal}
            </p>
            <p>
              <span className="font-medium">Date:</span>{" "}
              {new Date(order?.createdAt).toLocaleDateString("en-GB", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
          <p className="text-gray-500 text-xs mt-4">Order ID: {order._id}</p>
        </div>

        {/* Tickets Section */}
        <h1 className="text-3xl md:text-4xl font-serif text-[#4E5C36] mb-6 text-center md:text-left">
          Tickets
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {order?.tickets?.map((ticket: any, index: number) => (
            <div
              key={ticket.id}
              className="bg-white border border-gray-200 shadow-md p-6 flex flex-col items-center text-center"
            >
              <Link to={`${FRONTEND_URL}/ticket/${ticket.id}`}>
                <div className="mb-4">
                  <QRCode
                    value={`${FRONTEND_URL}/ticket/${ticket.id}`}
                    size={120}
                    bgColor="#ffffff"
                    fgColor="#4E5C36"
                  />
                </div>
              </Link>
              <h3 className="font-semibold text-lg text-[#4E5C36]">
                {`${ticket.firstName} ${ticket.lastName}`}
              </h3>
              <p className="text-sm text-gray-600">{ticket.email}</p>

              <p className="text-xs text-gray-500 mt-2">
                Ticket ID: {ticket.id}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
